import {
  OrganizationType,
  rdbV1OrganizationsDetail,
  type SlimOrganizationRes,
  v1ZipcodesSearchPostalCodeList,
} from '@on3/api';
import { type OptionType } from '@on3/ui-lib/components/AutoComplete/AutoComplete';
import { OrganizationAutoComplete } from '@on3/ui-lib/components/AutoComplete/OrganizationAutoComplete';
import { Checkbox } from '@on3/ui-lib/components/Checkbox/Checkbox';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { Select } from '@on3/ui-lib/components/Select/Select';
import { externalApi } from '@on3/ui-lib/index';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';

import { type ActionType, autoCompleteData, SearchKeys } from '..';
import styles from './LocationFilter.module.scss';

const distancesArray = [
  { label: 'Any Distance', value: '' },
  { label: '25', value: '25' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
  { label: '250', value: '250' },
  { label: '500', value: '500' },
];

interface ILocationFilterProps {
  dispatch: React.Dispatch<ActionType>;
  className?: string;
  distanceFromMiles?: number;
  distanceFromOrgData?: SlimOrganizationRes;
  distanceFromOrgKey?: number;
  distanceFromZipCode?: number;
}

export const LocationFilter = ({
  dispatch,
  distanceFromMiles,
  distanceFromOrgData,
  distanceFromOrgKey,
  distanceFromZipCode,
  className,
}: ILocationFilterProps) => {
  const [message, setMessage] = useState('');
  const [orgAutoData, setOrgAutoData] = useState<OptionType[] | undefined>(
    autoCompleteData(distanceFromOrgData),
  );
  const [zipCode, setZipCode] = useState<number | undefined>(
    distanceFromZipCode,
  );

  const handleClear = useCallback(() => {
    setOrgAutoData(undefined);
    setZipCode(undefined);

    dispatch({
      type: 'update',
      field: SearchKeys.DISTANCE_FROM_LATITUDE,
      value: '',
    });
    dispatch({
      type: 'update',
      field: SearchKeys.DISTANCE_FROM_LONGITUDE,
      value: '',
    });
    dispatch({
      type: 'update',
      field: SearchKeys.DISTANCE_FROM_ORG_KEY,
      value: '',
    });
    dispatch({
      type: 'update',
      field: SearchKeys.DISTANCE_FROM_ZIPCODE,
      value: '',
    });
  }, [dispatch]);

  const handleZipCodeChange = useCallback(
    async (value: number) => {
      setZipCode(value);
      setMessage('');

      try {
        let zipResponse;

        if (/^\d{5}$/.test(value.toString())) {
          zipResponse = await v1ZipcodesSearchPostalCodeList(externalApi, {
            postalCode: String(value),
          });
        }

        dispatch({
          type: 'update',
          field: SearchKeys.DISTANCE_FROM_LATITUDE,
          value: zipResponse?.latitude ?? '',
        });
        dispatch({
          type: 'update',
          field: SearchKeys.DISTANCE_FROM_LONGITUDE,
          value: zipResponse?.longitude ?? '',
        });

        if (zipResponse?.latitude && zipResponse.longitude) {
          dispatch({
            type: 'update',
            field: SearchKeys.DISTANCE_FROM_ZIPCODE,
            value: value ?? '',
          });
          setMessage('');
        } else {
          dispatch({
            type: 'update',
            field: SearchKeys.DISTANCE_FROM_ZIPCODE,
            value: '',
          });
        }
      } catch (error) {
        console.error('Error fetching zip code coordinates:', error);
        setMessage('Zip code not found');
      } finally {
        setOrgAutoData(undefined);
      }
    },

    [dispatch],
  );

  const handleHighSchoolSearch = useCallback(
    async (value: OptionType[]) => {
      const hsKey = value[0]?.key;

      if (!hsKey) return;

      setMessage('');

      try {
        let hsResponse;

        if (hsKey) {
          hsResponse = await rdbV1OrganizationsDetail(externalApi, hsKey);
        }

        dispatch({
          type: 'update',
          field: SearchKeys.DISTANCE_FROM_LATITUDE,
          value: hsResponse?.latitude ?? '',
        });
        dispatch({
          type: 'update',
          field: SearchKeys.DISTANCE_FROM_LONGITUDE,
          value: hsResponse?.longitude ?? '',
        });

        const coordinatesFound =
          !!hsResponse?.latitude && !!hsResponse?.longitude;

        hsResponse &&
          setMessage(coordinatesFound ? '' : 'High School location not found');

        dispatch({
          type: 'update',
          field: SearchKeys.DISTANCE_FROM_ORG_KEY,
          value: coordinatesFound && hsResponse?.key ? hsResponse.key : '',
        });

        if (hsResponse) {
          dispatch({
            type: 'update',
            field: SearchKeys.DISTANCE_FROM_ZIPCODE,
            value: '',
          });
          setZipCode(undefined);
        }
      } catch (error) {
        console.error('Error fetching high school coordinates:', error);
        setMessage('High School not found');
      }
    },
    [dispatch],
  );

  return (
    <div className={clsx(styles.block, className)} data-name="location">
      <h3>Location</h3>
      <div className={styles.distanceFrom}>
        <Select
          label="Distance"
          name={SearchKeys.DISTANCE_FROM_MILES}
          onChange={(e) =>
            dispatch({
              type: 'update',
              field: e.target.name,
              value: +e.target.value,
            })
          }
          options={distancesArray}
          srLabel
          value={String(distanceFromMiles)}
        />
        <div>
          <div className={styles.label}>miles</div>
          <div className={styles.label}>from:</div>
        </div>
      </div>

      <div className={styles.coordinates} data-enabled={!!distanceFromMiles}>
        <Checkbox
          disabled={!distanceFromZipCode}
          id="distanceFromZipCode"
          key={distanceFromZipCode}
          label="Zip Code"
          onChange={handleClear}
          srLabel
          value={!!distanceFromZipCode}
        />
        <Input
          disabled={!distanceFromMiles}
          label="Zip Code"
          name="zipCode"
          onChange={(e) => handleZipCodeChange(+e.target.value)}
          value={zipCode}
        />
      </div>

      <div className={styles.coordinates} data-enabled={!!distanceFromMiles}>
        <Checkbox
          disabled={!distanceFromOrgKey}
          id="distanceFromHighSchool"
          key={distanceFromOrgKey}
          label="High School"
          onChange={handleClear}
          srLabel
          value={!!distanceFromOrgKey}
        />
        <OrganizationAutoComplete
          disabled={!distanceFromMiles}
          id="HighSchool-zipcode"
          label="High School"
          limit={1}
          name="HighSchoolZipCode"
          onChange={handleHighSchoolSearch}
          type={OrganizationType.HighSchool}
          value={(orgAutoData ?? []).filter((o) => !!o?.key)}
        />
      </div>
      {message && <div className={styles.error}>{message}</div>}
    </div>
  );
};
